import { Fragment, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { paymentBankApi, type IncomeCheckResult } from '../../api/paymentBank.js';
import { ErrorBox } from '../../components/ui.js';
import { money } from '../../lib/format.js';
import { ApiError } from '../../api/client.js';

/**
 * 국민은행 중복 확인 — 원본 payment/bank_income_check.php mode=exec.
 *
 * 국민은행 엑셀에서 [No|거래일시|보낸분/받는분|출금액|입금액] 열만 복사해 붙여넣으면,
 * 서버가 (입금완료일 이후 + 금액 일치)로 등록된 입금건을 찾아준다. 예금주 일치 여부와
 * 중복 표시는 원본처럼 여기서 계산한다.
 */

function judge(result: IncomeCheckResult, match: IncomeCheckResult['matches'][number] | undefined) {
  if (!match) return { label: '보고건 없음', className: 'td-muted' };
  if (match.sender !== result.sender) return { label: '예금주 다름', className: 'badge badge--danger' };
  return { label: '매칭 완료', className: 'badge' };
}

export function BankIncomeCheckPage() {
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState<IncomeCheckResult[] | null>(null);

  const check = useMutation({
    mutationFn: () => paymentBankApi.incomeCheck(draft),
    onSuccess: (data) => setSubmitted(data.results),
  });

  return (
    <section>
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/reports/payment-bank">
            ← 입금건 관리
          </Link>
          <h1 className="page-title">국민은행 중복 확인</h1>
          <p className="page-desc">
            국민은행 엑셀파일 중 <code>No</code> · <code>거래일시</code> · <code>보낸분/받는분</code> ·{' '}
            <code>출금액(원)</code> · <code>입금액(원)</code> 열만 복사해 붙여넣으세요.
          </p>
        </div>
      </header>

      <textarea
        className="input input--area"
        style={{ height: 240, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={check.isPending || !draft.trim()}
          onClick={() => check.mutate()}
        >
          {check.isPending ? '확인 중…' : '체크하기'}
        </button>
      </div>

      {check.isError && (
        <ErrorBox error={check.error instanceof ApiError ? check.error : new Error('확인에 실패했습니다.')} />
      )}

      {submitted && (
        <div className="table-wrap" style={{ marginTop: 18 }}>
          <table className="table">
            <thead>
              <tr>
                <th>No</th>
                <th>거래일시</th>
                <th>받는분</th>
                <th style={{ textAlign: 'right' }}>출금액</th>
                <th style={{ textAlign: 'right' }}>금액</th>
                <th>예금주</th>
                <th>보고건등록일</th>
                <th>결과</th>
              </tr>
            </thead>
            <tbody>
              {submitted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="td-muted" style={{ textAlign: 'center' }}>
                    붙여넣은 내용이 없습니다.
                  </td>
                </tr>
              ) : (
                submitted.map((r, i) => {
                  const [first, ...rest] = r.matches;
                  const verdict = judge(r, first);
                  return (
                    <Fragment key={i}>
                      <tr className={rest.length > 0 ? 'row--total' : undefined}>
                        <td>{i + 1}</td>
                        <td>{r.date}</td>
                        <td>{r.sender}</td>
                        <td className="td-right">{r.outAmount !== null ? money(r.outAmount) : '-'}</td>
                        <td className="td-right">{first ? money(first.amount) : ''}</td>
                        <td>{first?.sender ?? ''}</td>
                        <td className="td-muted">{first?.reportDate ?? ''}</td>
                        <td>
                          <span className={verdict.className}>{verdict.label}</span>
                          {rest.length > 0 && (
                            <>
                              <br />
                              <span className="badge badge--warn">중복 {r.matches.length}건</span>
                            </>
                          )}
                        </td>
                      </tr>
                      {rest.map((m, j) => {
                        const v = judge(r, m);
                        return (
                          <tr key={`${i}-${j + 1}`}>
                            <td colSpan={4} />
                            <td className="td-right">{money(m.amount)}</td>
                            <td>{m.sender}</td>
                            <td className="td-muted">{m.reportDate}</td>
                            <td>
                              <span className={v.className}>{v.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
